import React, { useMemo } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { Control, Controller, useWatch } from 'react-hook-form';
import { Link } from '~/lib/react-router-dom';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Autocomplete, Box, Button, Link as MuiLink, Stack, TextField, Typography } from '@mui/material';

import { useGetCustomOffers } from '~/hooks/apis/offers/useGetCustomOffers';

import PermissionedComponent from '../PermissionedComponent';

import { CustomOfferStatusChip, CustomOfferVisibilityIcon } from './CustomOfferStatusAndVisibility';
import { CUSTOM_OFFER_PACKAGE_ADMIN_ROLES } from './customOfferPackagePermissions';

type PackageOfferOption = {
  id: string;
  name: string;
};

type LinkedPackageOfferFieldProps = {
  control: Control<CustomOfferFormValues>;
  currentOffer?: App.CustomOffer;
  userId: string;
  isSubmitting: boolean;
};

type PackageComponentsSectionProps = {
  currentOffer?: App.CustomOffer;
  userId: string;
};

function usePackageOfferOptions(currentOffer?: App.CustomOffer, userId?: string) {
  const { data: customOffersResponse, isLoading } = useGetCustomOffers(userId ?? '', !!userId);

  const packageOfferOptions = useMemo<Array<PackageOfferOption>>(() => {
    return (customOffersResponse?.result ?? [])
      .filter((customOffer: App.CustomOffer) => customOffer.items?.[0]?.type === 'custom-offer-package')
      .filter((customOffer: App.CustomOffer) => customOffer.id !== currentOffer?.id)
      .map((customOffer: App.CustomOffer) => ({
        id: customOffer.id,
        name: customOffer.name,
      }));
  }, [customOffersResponse?.result, currentOffer?.id]);

  return { isLoading, packageOfferOptions, customOffers: customOffersResponse?.result ?? [] };
}

export function CustomOfferLinkedPackageOfferField({
  control,
  currentOffer,
  userId,
  isSubmitting,
}: LinkedPackageOfferFieldProps) {
  const linkedPackageCustomOfferId = useWatch({ control, name: 'fk_package_custom_offer_id' });
  const { isLoading, packageOfferOptions } = usePackageOfferOptions(currentOffer, userId);

  const selectedPackageOfferOption = useMemo(
    () => packageOfferOptions.find((customOffer) => customOffer.id === linkedPackageCustomOfferId) ?? null,
    [linkedPackageCustomOfferId, packageOfferOptions],
  );

  return (
    <PermissionedComponent requiredRoles={[...CUSTOM_OFFER_PACKAGE_ADMIN_ROLES]}>
      <Controller
        name="fk_package_custom_offer_id"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            fullWidth
            options={packageOfferOptions}
            value={selectedPackageOfferOption}
            onChange={(_, option) => field.onChange(option?.id ?? null)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoading}
            disabled={isSubmitting}
            noOptionsText="No package offers found for this customer"
            renderInput={(params) => (
              <TextField {...params} label="Linked package offer" helperText={error?.message} error={!!error} />
            )}
            data-testid="linked-package-offer"
          />
        )}
      />
    </PermissionedComponent>
  );
}

export function CustomOfferPackageComponentsSection({ currentOffer, userId }: PackageComponentsSectionProps) {
  const { isLoading, customOffers } = usePackageOfferOptions(currentOffer, userId);

  const packageComponentOffers = useMemo<Array<App.CustomOffer>>(() => {
    if (!currentOffer?.id) {
      return [];
    }

    const packageComponentIds = new Set(currentOffer.package_component_ids ?? []);

    return customOffers
      .filter((customOffer: App.CustomOffer) => customOffer.id !== currentOffer.id)
      .filter((customOffer: App.CustomOffer) =>
        packageComponentIds.size > 0
          ? packageComponentIds.has(customOffer.id)
          : customOffer.fk_package_custom_offer_id === currentOffer.id,
      )
      .sort((firstOffer: App.CustomOffer, secondOffer: App.CustomOffer) =>
        firstOffer.name.localeCompare(secondOffer.name),
      );
  }, [customOffers, currentOffer?.id, currentOffer?.package_component_ids]);

  return (
    <Box mt={2}>
      <Typography variant="h6">Package components</Typography>
      <Box mt={1}>
        {packageComponentOffers.length > 0 ? (
          <Stack spacing={1}>
            {packageComponentOffers.map((componentOffer) => (
              <Box
                key={componentOffer.id}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Box>
                    <MuiLink
                      component={Link}
                      to={`/users/${userId}/custom-offers/${componentOffer.id}`}
                      underline="hover"
                    >
                      {componentOffer.name}
                    </MuiLink>
                    <Typography variant="body2" color="text.secondary">
                      {componentOffer.items?.[0]?.type ?? 'custom-offer'} | {componentOffer.id}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    justifyContent="flex-end"
                  >
                    <CustomOfferVisibilityIcon hiddenFromCustomer={componentOffer.hidden_from_customer} />
                    <CustomOfferStatusChip status={componentOffer.status} />
                    <Button
                      type="button"
                      variant="text"
                      component={Link}
                      to={`/users/${userId}/custom-offers/${componentOffer.id}`}
                      endIcon={<OpenInNewIcon fontSize="small" />}
                    >
                      Open
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <Alert severity="info">
            {isLoading ? 'Loading linked component offers...' : 'No component offers are linked to this package yet.'}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
